/* Created by Interactive 63 - https://interactive63.com */
/**
 * Edits that change the artwork itself rather than adding animation. A gradient stop's colour
 * and a text element's font size and words, while they have no animation, are rewritten in the
 * SVG source, so they can be changed without creating a keyframe.
 */
import type { ElementId, Project, PropertyKey, PropertyValue } from '../model/types';
import { setTextContent, setTextStyle, TEXT_TAGS } from '../text/text';

/** Properties that are written into the source when they are not animated, and their attributes. */
const STATIC_ATTRIBUTES: Partial<Record<PropertyKey, string>> = {
  stopColor: 'stop-color',
  stopOpacity: 'stop-opacity',
};

/** Text properties that are written into the source when they are not animated. */
const STATIC_TEXT_PROPERTIES = new Set<PropertyKey>(['fontSize', 'words']);

/** Whether a property on an element can be edited in the source instead of keyed. */
export function canSetStatically(project: Project, id: ElementId, property: PropertyKey): boolean {
  const tag = project.elements[id]?.tag ?? '';
  if (TEXT_TAGS.has(tag)) return STATIC_TEXT_PROPERTIES.has(property);
  return tag === 'stop' && STATIC_ATTRIBUTES[property] !== undefined;
}

/** Removes one declaration from an inline style, since it would override the attribute. */
function withoutDeclaration(style: string, name: string): string {
  return style
    .split(';')
    .filter((part) => part.split(':')[0]?.trim().toLowerCase() !== name)
    .join(';')
    .trim();
}

/**
 * Writes a value into an element's attribute in the SVG source. Returns false, changing nothing,
 * when the property cannot be set this way or the element is missing.
 */
export function setStaticValue(
  draft: Project,
  id: ElementId,
  property: PropertyKey,
  value: PropertyValue,
): boolean {
  if (!canSetStatically(draft, id, property)) return false;
  if (property === 'words') return typeof value === 'string' && setTextContent(draft, id, value);
  if (property === 'fontSize') {
    return typeof value === 'number' && value > 0 && setTextStyle(draft, id, { fontSize: value });
  }
  const attribute = STATIC_ATTRIBUTES[property];
  if (!attribute) return false;
  if (typeof value !== 'string' && typeof value !== 'number') return false;
  const doc = new DOMParser().parseFromString(draft.svg.source, 'image/svg+xml');
  const node = doc.querySelector(`[data-st-id="${id}"]`);
  if (!node) return false;
  node.setAttribute(attribute, String(value));
  const style = node.getAttribute('style');
  if (style !== null) {
    const rest = withoutDeclaration(style, attribute);
    if (rest === '') node.removeAttribute('style');
    else node.setAttribute('style', rest);
  }
  draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);
  return true;
}
